
import { useState, useEffect, useRef } from 'react';

export default function AISection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredService, setHoveredService] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      title: 'AI & Agentic Solutions',
      description: 'Automate processes and enhance decision-making with cutting-edge AI technology.',
      icon: 'ri-robot-line',
      link: '/services/ai'
    },
    {
      title: 'AI Integrated Social Media Marketing',
      description: 'Leverage AI to amplify your brand\'s voice and drive meaningful social connections.',
      icon: 'ri-share-line',
      link: '/services/ai-social-media'
    },
    {
      title: 'AI Optimized PPC Campaigns',
      description: 'Precision advertising with AI-powered optimization for maximum ROI and conversions.',
      icon: 'ri-advertisement-line',
      link: '/services/ai-ppc'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      id="ai"
      className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-10 w-32 h-32 bg-pink-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-purple-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            AI & Agentic 
            <span className="bg-gradient-to-r from-pink-400 to-purple-500 bg-clip-text text-transparent"> Solutions</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Empowering efficiency through intelligent automation. Transform your business processes with cutting-edge AI technology that enhances decision-making and drives innovation.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <a
              key={index}
              href={service.link}
              className={`group block transition-all duration-700 delay-${index * 100} ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              onMouseEnter={() => setHoveredService(index)}
              onMouseLeave={() => setHoveredService(null)}
            >
              <div className="relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-pink-500/20 hover:border-pink-500/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-pink-500/20 cursor-pointer h-full">
                {/* Hover Glow Effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-500 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
                
                {/* Icon */}
                <div className="relative w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center group-hover:animate-pulse">
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-4 text-center group-hover:text-pink-400 transition-colors duration-300">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-gray-300 text-center text-sm leading-relaxed group-hover:text-white transition-colors duration-300">
                  {service.description}
                </p>

                {/* Learn More Button */}
                <div className="mt-6 text-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <span className="inline-flex items-center text-pink-400 font-semibold">
                    Learn More
                    <i className="ri-arrow-right-line ml-2"></i>
                  </span>
                </div>

                {/* Hover Arrow */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                  <i className="ri-arrow-right-line text-pink-400 text-xl"></i>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <a
            href="/services/ai"
            className="inline-flex items-center bg-gradient-to-r from-pink-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-pink-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-pink-500/25"
          >
            Explore All AI Solutions
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
